import React, { createContext, useCallback, useContext, useMemo, useState } from "react"
import { rewriteSlug } from "../utils/rewriteSlug"
import { ChildrenProp } from "../../react"
import { locales, Locale } from "../../locales"
import { useSitePages } from "./SitePagesContext"

type Links = {
  [locale in Locale]: string
}

interface TranslatedLinks {
  links: Links
  changeTranslatedLinks: (_story: Story) => void
}

export const TranslatedLinksContext = createContext<TranslatedLinks | null>(null)

export const TranslatedLinksProvider: React.FC<ChildrenProp> = ({ children }) => {
  // Initialize links dynamically based on locales.ts
  const initialLinks: Links = locales.reduce<Links>((acc, locale) => {
    acc[locale] = locale === "default" ? "/" : `/${locale}`
    return acc
  }, {} as Links)

  const [links, setLinks] = useState<Links>(initialLinks)

  const { isSitePage } = useSitePages()

  const changeTranslatedLinks = useCallback(
    (story: Story) => {
      const newLinks = { ...initialLinks }
      story.translated_slugs.forEach(({ path, lang }) => {
        // Update links for valid site pages
        if (isSitePage(`${lang}/${path}`))
          newLinks[lang] = rewriteSlug(`${lang}/${path}`)
      })

      // Handle the default language
      if (isSitePage(story.default_full_slug))
        newLinks.default = rewriteSlug(story.default_full_slug)

      setLinks(newLinks)
    },
    [isSitePage, initialLinks]
  )

  const contextValue = useMemo(
    () => ({
      changeTranslatedLinks,
      links,
    }),
    [changeTranslatedLinks, links]
  )

  return (
    <TranslatedLinksContext.Provider value={contextValue}>
      {children}
    </TranslatedLinksContext.Provider>
  )
}

interface Story {
  default_full_slug: string
  translated_slugs: {
    path: string
    lang: Locale
  }[]
}

const useTranslatedLinks = (): TranslatedLinks => {
  const context = useContext(TranslatedLinksContext)
  if (!context) throw new Error("useTranslatedLinks must be used within a TranslatedLinksProvider")
  return context
}

export default useTranslatedLinks
